import type { IntakeSpec } from '@shared/types';
import {
  DesignSpecSchema,
  type DesignSpec,
  type SpecParameter,
  type Material,
  type ViewHint,
} from '@shared/spec';
import {
  derivePartsForSubType,
  deriveJointsForSubType,
  deriveFeaturesForSubType,
} from '@/lib/seedDispatch';
import i18n from '@/i18n/i18n';

function deriveTitle(intake: IntakeSpec): string {
  const trimmed = intake.core.intendedUse.trim();
  if (trimmed.length > 60) return trimmed.slice(0, 57) + '…';
  return trimmed || i18n.t('services.untitledDesign', { ns: 'common' });
}

function deriveParameters(intake: IntakeSpec): SpecParameter[] {
  const params: SpecParameter[] = [];
  // Use the localized "size" label as the group name for dimension parameters.
  const dimensionsGroup = i18n.t('coreLabels.size', { ns: 'intake' });
  if (intake.core.size?.length != null) {
    params.push({
      name: 'total_length',
      displayName: i18n.t('dimensions.length', { ns: 'intake' }),
      value: intake.core.size.length,
      defaultValue: intake.core.size.length,
      type: 'number',
      group: dimensionsGroup,
    });
  }
  if (intake.core.size?.width != null) {
    params.push({
      name: 'total_width',
      displayName: i18n.t('dimensions.width', { ns: 'intake' }),
      value: intake.core.size.width,
      defaultValue: intake.core.size.width,
      type: 'number',
      group: dimensionsGroup,
    });
  }
  if (intake.core.size?.height != null) {
    params.push({
      name: 'total_height',
      displayName: i18n.t('dimensions.height', { ns: 'intake' }),
      value: intake.core.size.height,
      defaultValue: intake.core.size.height,
      type: 'number',
      group: dimensionsGroup,
    });
  }
  return params;
}

function deriveMaterials(intake: IntakeSpec): Material[] {
  const kind = intake.core.primaryMaterial;
  if (!kind) return [];
  const defaultThickness: Record<string, number> = {
    plywood: 18,
    hardwood: 25,
    softwood: 25,
    mdf: 18,
    sheet_metal: 2,
    printed_pla: 2,
    printed_abs: 2,
    printed_petg: 2,
  };
  // The material kind is itself a key in the optionLabels resource block.
  const name = i18n.t(`optionLabels.${kind}`, { ns: 'intake' }) || kind;
  const thickness = defaultThickness[kind];
  return [
    {
      id: 'mat-primary',
      kind,
      name,
      ...(thickness != null ? { thickness } : {}),
    },
  ];
}

function deriveViewHints(intake: IntakeSpec): ViewHint[] {
  const front = i18n.t('spec.viewFront', { ns: 'common' });
  const top = i18n.t('spec.viewTop', { ns: 'common' });
  const right = i18n.t('spec.viewRight', { ns: 'common' });
  const iso = i18n.t('spec.viewIso', { ns: 'common' });
  const layout = i18n.t('spec.viewLayout', { ns: 'common' });
  switch (intake.category) {
    case 'mechanical':
    case 'furniture':
      return [
        { label: front, camera: 'front' },
        { label: top, camera: 'top' },
        { label: right, camera: 'right' },
        { label: iso, camera: 'isometric' },
      ];
    case 'architecture':
      return [
        { label: iso, camera: 'isometric' },
        { label: layout, camera: 'top' },
      ];
    case 'printing':
      return [{ label: iso, camera: 'isometric' }];
  }
}

export function intakeToSeedSpec(intake: IntakeSpec): DesignSpec {
  const parts = derivePartsForSubType(intake);
  const joints = deriveJointsForSubType(intake);
  const features = deriveFeaturesForSubType(intake);

  const base = {
    specVersion: '1' as const,
    title: deriveTitle(intake),
    units: 'mm' as const,
    qualityLevel: intake.qualityLevel,
    parameters: deriveParameters(intake),
    materials: deriveMaterials(intake),
    parts,
    features,
    joints,
    viewHints: deriveViewHints(intake),
  };
  return DesignSpecSchema.parse(base);
}
